import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useIntl } from 'react-intl';
import {
	graphql,
	type PreloadedQuery,
	useFragment,
	usePaginationFragment,
	usePreloadedQuery,
} from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import { AnyAri } from '@atlassian/ari/any-ari';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import type { IssueLinkedPRs } from '@atlassian/search-client/hydration/pr-types';
import type { PastMeetingOccurrencesQueryType } from '@atlassian/search-client/past-meeting-occurrences/fetch';
import type { PastMeetingOccurrencesMap } from '@atlassian/search-client/past-meeting-occurrences/types';
import { ResourceType } from '@atlassian/search-client/third-party-graph-entity';
import { isAnyCodeSearchEnabled } from '@atlassian/search-common/code-search-mode';

import { TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { is3pProductKeyForAnalytics } from '../../../common/constants/is3p-product-key-for-analytics';
import { isProductKey, ProductKeys } from '../../../common/constants/product-keys';
import { products1P } from '../../../common/constants/products';
import { ResultsStates } from '../../../common/constants/results';
import { type ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { IAnalyticsSearchPageResults } from '../../../common/types';
import { type DebugResult } from '../../../common/ui/debug-overrides-panel';
import { DebugOverridesPanel } from '../../../common/ui/debug-overrides-panel/async';
import { EMBEDDED_COMMENTS_DEFAULT_MAX } from '../../../common/ui/embedded-comments';
import { getLinksFromText } from '../../../common/ui/link-attachments';
import {
	LINKED_RESULTS_DEFAULT_EXPAND_SIZE,
	LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND,
} from '../../../common/ui/linked-results';
import type { ResultShown } from '../../../common/utils/events/types';
import { triggerExposureForTeamsImpersonatedSearchExp } from '../../../common/utils/experiments';
import { isIntentDetectionTriggeredForExperiment } from '../../../common/utils/experiments/experiment-exports';
import { serveFederatedSlackExperiment } from '../../../common/utils/experiments/experiment-utils';
import {
	MICROSOFT_INTEGRATION_ARI,
	ZOOM_INTEGRATION_ARI,
} from '../../../common/utils/filters/integration-aris';
import { getIntegrationKey } from '../../../common/utils/get-integration-key';
import { getProductFromProviderId } from '../../../common/utils/get-product-from-provider-id';
import { scmToProductKey } from '../../../common/utils/scm-to-product-key';
import { get3PProductFromProviderIdOrIntegrationId } from '../../../common/utils/search-result-utils';
import { shouldHydrateJiraGitHubPRs } from '../../../common/utils/should-hydrate-jira-git-hub-prs';
import { getTeamId } from '../../../common/utils/teams';
import { getResultConnectorSource } from '../../../common/utils/third-party-utils';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import useDwellTime from '../../../controllers/dwell';
import { useFeatures } from '../../../controllers/store/bootstrap';
import {
	useSearchActions,
	useWasContentClearedBySpaceChange,
} from '../../../controllers/store/search';
import { filter as spaceFilter } from '../../../controllers/store/search/filters/space';
import type { searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData$key } from './__generated__/searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData.graphql';
import type { searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$key } from './__generated__/searchResults_AtlassianSearchPage_SearchResultsBody_connectionData.graphql';
import type { SearchResultsAsyncPaginationQuery } from './__generated__/SearchResultsAsyncPaginationQuery.graphql';
import type { searchResultsAsyncQuery } from './__generated__/searchResultsAsyncQuery.graphql';
import AutoCorrection from './auto-correction';
import { fetchAndUpdateJiraFields } from './fetch-and-update-jira-fields';
import { fetchAndUpdateJiraLinkedPRs } from './fetch-and-update-jira-linked-p-rs';
import { fetchAndUpdatePastMeetingOccurrences } from './fetch-and-update-past-meeting-occurrences';
import { fetchAndUpdateTeamNames } from './fetch-and-update-team-names';
import { getJiraIssueHydrationInputs } from './get-jira-issue-hydration-inputs';
import { ProductOauthUpgradeBanner } from './product-oauth-upgrade-banner';
import { ResultDerivedData } from './result-derived-data';
import { SearchResultsListLiteRenderer } from './search-result-list-lite-renderer';
import { isSearchCompassComponent } from './search-result/search-result-renderer/search-compass-component';
import {
	isSearchJiraIssue,
	type JiraIssueStatusAndColorName,
} from './search-result/search-result-renderer/search-jira-issue';
import { SearchResultsListDefaultRenderer } from './search-results-list-default-renderer';
import type { SearchResultsProps } from './types';
import { useAfterQueryPerformanceTiming } from './use-after-query-performance-timing';
import { useConfluenceAuthorHydration } from './use-confluence-author-hydration';
import { useFederatedEmailResultCount } from './use-federated-email-result-count';
import { useRenderResultsOptimisation } from './use-render-results-optimisation';
import { useResultFieldDiagnostics } from './use-result-field-diagnostics';
import { useResultRowSubscriptionHoistingExperiment } from './use-result-row-subscription-hoisting-experiment';
import { useSearchResultsAnalytics } from './use-search-results-analytics';
import { useSearchResultsVitalStats } from './use-search-results-vital-stats';

const PAGINATION_UNUSED_PAGE_SIZE = 30;

type SearchResultsSharedProps = Omit<SearchResultsProps, 'searchResultsAsyncQuery'>;

type SearchResultsAsyncInnerProps = {
	queryData: searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData$key;
	queryReference: PreloadedQuery<searchResultsAsyncQuery>;
	sharedProps: SearchResultsSharedProps;
};

type SearchResultsBodyProps = {
	connectionData: searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$key | null;
	isCachedResult?: boolean | null;
	isLoadingNext: boolean;
	hasPreviousRef: React.MutableRefObject<boolean>;
	cursor: string | null | undefined;
	fetchNextPage: (nextCursor: string) => void;
	queryReference: PreloadedQuery<searchResultsAsyncQuery>;
	sharedProps: SearchResultsSharedProps;
};

/**
 * Three-tier construction:
 * - SearchResultsAsync = toplevel usePreloadedQuery() - handles preloading, variable inputs, query-plan
 * - SearchResultsAsyncInner = usePaginationFragment - defines the actual asyncSearch() query, handles pagination
 * - SearchResultsBody = defines the actual fields for the query / response
 *
 *   SearchResultsAsync
 *   └── SearchResultsAsyncInner
 *   	└── SearchResultsBody
 */
const SearchResultsAsync = ({
	searchResultsAsyncQuery: searchResultsAsyncQueryRef,
	...sharedProps
}: SearchResultsProps): React.JSX.Element => {
	const queryData = usePreloadedQuery<searchResultsAsyncQuery>(
		graphql`
			query searchResultsAsyncQuery(
				$query: String!
				$cloudIdARIs: [String!]!
				$tenantId: ID!
				$limit: Int!
				$entities: [String!]!
				$queryVersion: Int
				$searchSessionId: String
				$sessionId: String
				$commonFilters: SearchCommonFilter
				$confluenceFilters: SearchConfluenceFilter
				$jiraFilters: SearchJiraFilter
				$loomFilters: SearchLoomFilters
				$assetsFilters: SearchAssetsFilter
				$mercuryFilters: SearchMercuryFilter
				$talentFilters: SearchTalentFilter
				$compassFilters: SearchCompassFilter
				$thirdPartyFilters: SearchThirdPartyFilter
				$sort: [SearchSortInput]
				$sourceProduct: String
				$experimentId: String
				$shadowExperimentId: String
				$experimentLayers: [SearchExperimentLayer]
				$matchingLayerExperimentsMetadata: SearchMatchingLayerExperimentsMetadata
				$after: String
				$disableQueryReplacement: Boolean
				$experience: String!
				$useCaseId: String
				$enableHighlighting: Boolean
				# Not populated by the FE on purpose: this variable exists only so the
				# outgoing AGG request can be intercepted browser-side and have relevance
				# debugging switched on. Do not remove even though no caller sets it.
				$enableRelevanceDebugging: Boolean
				$fanoutExperimentCohort: String
				$expectResult: Boolean
				$includePositions: Boolean = false
				$includeThirdPartyId: Boolean = false
				$includeDatasourceId: Boolean = false
				$includeNavBoostScore: Boolean = false
				$includePassionfruitAsk: Boolean = false
				$includeRisk: Boolean = false
				$includeMercuryAsk: Boolean = false
				$includeJiraLiteModeFields: Boolean = false
				$includeFastJiraHydration: Boolean = false
				$includeCodeSearch: Boolean = false
				$includeCaseEmbeddedComments: Boolean = false
			) {
				...searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData
					@arguments(
						useCaseId: $useCaseId
						includePositions: $includePositions
						includeThirdPartyId: $includeThirdPartyId
						includeDatasourceId: $includeDatasourceId
						includeNavBoostScore: $includeNavBoostScore
						includePassionfruitAsk: $includePassionfruitAsk
						includeRisk: $includeRisk
						includeMercuryAsk: $includeMercuryAsk
						includeJiraLiteModeFields: $includeJiraLiteModeFields
						includeFastJiraHydration: $includeFastJiraHydration
						includeCodeSearch: $includeCodeSearch
						includeCaseEmbeddedComments: $includeCaseEmbeddedComments
					)
			}
		`,
		searchResultsAsyncQueryRef,
	);

	const inner = (
		<SearchResultsAsyncInner
			queryData={queryData}
			queryReference={searchResultsAsyncQueryRef}
			sharedProps={sharedProps}
		/>
	);

	return inner;
};

export { SearchResultsAsync };

const SearchResultsAsyncInner = ({
	queryData,
	queryReference,
	sharedProps,
}: SearchResultsAsyncInnerProps): React.JSX.Element => {
	const hasPreviousRef = useRef(false);

	const { data, loadNext, isLoadingNext } = usePaginationFragment<
		SearchResultsAsyncPaginationQuery,
		searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData$key
	>(
		graphql`
			fragment searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData on Query
			@refetchable(queryName: "SearchResultsAsyncPaginationQuery")
			@argumentDefinitions(
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includeFastJiraHydration: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
				includeRisk: { type: "Boolean", defaultValue: false }
				includeMercuryAsk: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeDatasourceId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
				includeCodeSearch: { type: "Boolean", defaultValue: false }
				includeCaseEmbeddedComments: { type: "Boolean", defaultValue: false }
				useCaseId: { type: "String" }
			) {
				search {
					search: asyncSearch(
						experience: $experience
						useCaseId: $useCaseId
						enableHighlighting: $enableHighlighting
						enableRelevanceDebugging: $enableRelevanceDebugging
						query: $query
						first: $limit
						after: $after
						sort: $sort
						disableQueryReplacement: $disableQueryReplacement
						fanoutExperimentCohort: $fanoutExperimentCohort
						expectResult: $expectResult
						filters: {
							entities: $entities
							locations: $cloudIdARIs
							commonFilters: $commonFilters
							confluenceFilters: $confluenceFilters
							jiraFilters: $jiraFilters
							loomFilters: $loomFilters
							mercuryFilters: $mercuryFilters
							compassFilters: $compassFilters
							assetsFilters: $assetsFilters
							thirdPartyFilters: $thirdPartyFilters
							talentFilters: $talentFilters
						}
						analytics: {
							queryVersion: $queryVersion
							searchSessionId: $searchSessionId
							sourceProduct: $sourceProduct
							sessionId: $sessionId
						}
						experimentContext: {
							experimentId: $experimentId
							shadowExperimentId: $shadowExperimentId
							experimentLayers: $experimentLayers
						}
						matchingLayerExperimentsMetadata: $matchingLayerExperimentsMetadata
					) @connection(key: "SearchResultsAsyncPagination__search") {
						isCachedResult
						...searchResults_AtlassianSearchPage_SearchResultsBody_connectionData
							@arguments(
								includeJiraLiteModeFields: $includeJiraLiteModeFields
								includeFastJiraHydration: $includeFastJiraHydration
								includePassionfruitAsk: $includePassionfruitAsk
								includePositions: $includePositions
								includeThirdPartyId: $includeThirdPartyId
								includeDatasourceId: $includeDatasourceId
								includeNavBoostScore: $includeNavBoostScore
								includeCodeSearch: $includeCodeSearch
								includeRisk: $includeRisk
								includeMercuryAsk: $includeMercuryAsk
								includeCaseEmbeddedComments: $includeCaseEmbeddedComments
							)
						edges {
							node {
								__typename
							}
						}
						pageInfo {
							endCursor
							hasNextPage
						}
					}
				}
			}
		`,
		queryData,
	);

	const cursor = data.search?.search?.pageInfo?.endCursor;
	const fetchNextPage = useCallback(
		(nextCursor: string) => {
			if (nextCursor === cursor) {
				loadNext(PAGINATION_UNUSED_PAGE_SIZE);
				hasPreviousRef.current = true;
			}
		},
		[loadNext, cursor],
	);

	return (
		<SearchResultsBody
			connectionData={data.search?.search ?? null}
			isCachedResult={data.search?.search?.isCachedResult}
			isLoadingNext={isLoadingNext}
			hasPreviousRef={hasPreviousRef}
			cursor={cursor}
			fetchNextPage={fetchNextPage}
			queryReference={queryReference}
			sharedProps={sharedProps}
		/>
	);
};

const SearchResultsBody = ({
	connectionData,
	isCachedResult,
	isLoadingNext,
	hasPreviousRef,
	cursor,
	fetchNextPage,
	queryReference,
	sharedProps,
}: SearchResultsBodyProps): React.JSX.Element => {
	const {
		aggAbsoluteUrl,
		cloudId,
		thirdPartyConfigs,
		selectedProduct,
		primaryProduct,
		primarySubProduct,
		appMode,
		searchSessionId,
		searchQuery,
		availableProducts,
		experimentConfig,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
		setResultsState,
		getSelectedFilters,
		clearFilters,
		logError,
		clearReferrer,
		referrer,
		autoCorrectPortalRef,
		smartAnswersPortalRef,
		debugPortalRef,
		setFederatedSlackCount,
		setFederatedOutlookMailCount,
		setFederatedGmailCount,
		setHasMoreGmailResults,
		setHasMoreOutlookMailResults,
		setProductsWithTimeout,
		bodyRef,
		maxResultsToShow,
		setMaxResultsToShow,
		mainContentWidth,
		backendExperimentRef,
		renderConnectorPromo,
		selectedSites,
	} = sharedProps;

	// Fragment for the search result body content within the connection
	const bodyData =
		useFragment<searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$key>(
			graphql`
				fragment searchResults_AtlassianSearchPage_SearchResultsBody_connectionData on SearchItemConnection
				@argumentDefinitions(
					includeAssets: { type: "Boolean!", provider: "./is-assets-enabled.relayprovider" }
					includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
					includeFastJiraHydration: { type: "Boolean", defaultValue: false }
					includePassionfruitAsk: { type: "Boolean", defaultValue: false }
					includeRisk: { type: "Boolean", defaultValue: false }
					includeMercuryAsk: { type: "Boolean", defaultValue: false }
					includePositions: { type: "Boolean", defaultValue: false }
					includeThirdPartyId: { type: "Boolean", defaultValue: false }
					includeDatasourceId: { type: "Boolean", defaultValue: false }
					includeNavBoostScore: { type: "Boolean", defaultValue: false }
					includeCodeSearch: { type: "Boolean", defaultValue: false }
					includeCaseEmbeddedComments: { type: "Boolean", defaultValue: false }
				) {
					edges {
						node {
							__typename
							id
							description
							linkedResults {
								id
								datasourceId @include(if: $includeDatasourceId)
								category
								connectorType
							}
							...searchResult_AtlassianSearchPage
								@arguments(
									includeAssets: $includeAssets
									includeJiraLiteModeFields: $includeJiraLiteModeFields
									includeFastJiraHydration: $includeFastJiraHydration
									includePassionfruitAsk: $includePassionfruitAsk
									includeRisk: $includeRisk
									includeMercuryAsk: $includeMercuryAsk
									includePositions: $includePositions
									includeThirdPartyId: $includeThirdPartyId
									includeDatasourceId: $includeDatasourceId
									includeNavBoostScore: $includeNavBoostScore
									includeCodeSearch: $includeCodeSearch
									includeCaseEmbeddedComments: $includeCaseEmbeddedComments
								)
							... on SearchResultGraphDocument {
								datasourceId @include(if: $includeDatasourceId)
								# required by analytics
								providerId
								integrationId
								connectorType
								connectorId
								connectorProduct
								linkedEntities {
									id
								}
							}
							... on SearchResultGraphCode @include(if: $includeCodeSearch) {
								# required by analytics: attribute code results to their code host
								repository {
									scm
								}
							}
							... on SearchResultCompassComponent {
								# we need this in this file so we can pass down the lazy-loaded team name
								ownerId
							}
							... on SearchResultJiraIssue {
								# we need this in this file so we can pass down the lazy-loaded issue status
								url
								issue {
									webUrl
								}
							}
							... on SearchConfluencePageBlogAttachment {
								# Used in this file by useConfluenceAuthorHydration to avoid hydrating attachments.
								# eslint-disable-next-line @atlassian/relay/unused-fields
								type
								# required by analytics
								socialSignal(tenantId: $tenantId) {
									signal
								}
							}
						}
					}
					queryInfo {
						originalQuery
						...autoCorrection_AtlassianSearchPage
					}
					pageInfo {
						endCursor
						hasNextPage
					}
					traceId
					errors {
						statusCode
						product
						integration
					}
				}
			`,
			connectionData,
		);

	// Capture data availability before deduplication and result preparation.
	const dataAvailableAt = performance.now();

	// It is possible that aggregator returns the same node twice, but then <SearchResult key={edge.node.id} /> breaks
	// So we need to dedup by id
	const edges = useMemo(() => {
		const seenIds = new Set();
		const edges = bodyData?.edges || [];
		return edges.filter((edge) => {
			const id = edge?.node?.id;
			if (seenIds.has(id)) {
				return false;
			} else {
				seenIds.add(id);
				return true;
			}
		});
	}, [bodyData?.edges]);

	useConfluenceAuthorHydration({
		edges,
		cloudId,
		aggAbsoluteUrl,
		logError,
		product: primaryProduct,
	});

	const edgesToDisplay = useRenderResultsOptimisation({
		bodyRef,
		edges,
		maxResultsToShow,
		setMaxResultsToShow,
		queryReference,
	});

	const [features] = useFeatures();
	const isLiteMode = features?.lite?.enabled ?? false;

	const productsWithTimeoutsFromErrors: ProductKeys[] | undefined = useMemo(() => {
		const errors = bodyData?.errors || [];

		return errors
			.filter((e: { statusCode: number }) => e.statusCode === 408)
			.map((e) => e.integration ?? e.product)
			.flatMap((s) => (s && isProductKey(s) ? [s] : [])); // Flatmap for better type inference
	}, [bodyData?.errors]);

	useEffect(() => {
		// We only want to set products with timeout in all product search
		if (selectedProduct !== undefined) {
			return;
		}

		setProductsWithTimeout?.(productsWithTimeoutsFromErrors);
	}, [productsWithTimeoutsFromErrors, selectedProduct, setProductsWithTimeout]);

	const resultsForAnalytics = useMemo(() => transformResultsForAnalytics(edges), [edges]);
	const resultsLength = edges.length;
	const rawResultsLength = bodyData?.edges?.length ?? 0;

	// Set federated Slack count when selectedProduct is Slack
	useEffect(() => {
		const isSlackFederatedSearchEnabled = serveFederatedSlackExperiment(
			selectedProduct,
			undefined,
			{ fireExperimentExposure: false },
			thirdPartyConfigs,
		);
		if (
			selectedProduct === ProductKeys.Slack &&
			setFederatedSlackCount &&
			isSlackFederatedSearchEnabled
		) {
			setFederatedSlackCount(resultsLength);
		}
	}, [selectedProduct, resultsLength, setFederatedSlackCount, thirdPartyConfigs]);

	useFederatedEmailResultCount({
		selectedProduct,
		rawResultsLength,
		resultsLength,
		pageInfo: bodyData?.pageInfo,
		isLoadingNext,
		setFederatedGmailCount,
		setHasMoreGmailResults,
		setFederatedOutlookMailCount,
		setHasMoreOutlookMailResults,
	});

	// Records dwell time for search results
	// TODO: refactor this to use props instead of RSS
	useDwellTime();

	// Compass team names
	const [teamNames, setTeamNames] = useState<Record<string, string | null>>({});

	const { teamIds, nodeIdMap } = useMemo(
		() => getCompassTeamIds(edges, selectedProduct),
		[edges, selectedProduct],
	);

	useEffect(() => {
		fetchAndUpdateTeamNames(cloudId, teamIds, nodeIdMap, setTeamNames, logError);
	}, [cloudId, teamIds, nodeIdMap, logError]);

	// Jira issue statuses
	const [issueStatuses, setIssueStatuses] = useState<
		Record<string, JiraIssueStatusAndColorName | null>
	>({});

	const { issueKeys, issueKeysByCloudId } = useMemo(
		() => getJiraIssueHydrationInputs(getJiraIssueHydrationItems(edges)),
		[edges],
	);

	useEffect(() => {
		fetchAndUpdateJiraFields(
			cloudId,
			issueKeys,
			aggAbsoluteUrl,
			setIssueStatuses,
			logError,
			undefined,
			primaryProduct,
			issueKeysByCloudId,
		);
	}, [
		cloudId,
		issueKeys,
		aggAbsoluteUrl,
		setIssueStatuses,
		logError,
		primaryProduct,
		issueKeysByCloudId,
	]);

	// Past meeting occurrences hydration
	// Fetches up to 5 past occurrences of a recurring meeting via TWG Cypher query.
	// Only runs for supported video integration results (identified by integrationId).
	const [pastMeetingOccurrences, setPastMeetingOccurrences] = useState<PastMeetingOccurrencesMap>(
		{},
	);
	const isZoomRefreshEnabled = fg('rovo_fps_zoom_refresh');
	const pastMeetingVideoHydrationItems = useMemo(
		() => getPastMeetingVideoHydrationItems(edges, isZoomRefreshEnabled),
		[edges, isZoomRefreshEnabled],
	);
	const fetchedPastMeetingArisRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		const newItems = pastMeetingVideoHydrationItems.filter(
			(item) => !fetchedPastMeetingArisRef.current.has(item.videoAri),
		);
		if (newItems.length === 0) {
			return;
		}
		for (const item of newItems) {
			fetchedPastMeetingArisRef.current.add(item.videoAri);
		}
		fetchAndUpdatePastMeetingOccurrences(
			newItems,
			cloudId,
			aggAbsoluteUrl,
			setPastMeetingOccurrences,
			logError,
		);
	}, [pastMeetingVideoHydrationItems, cloudId, aggAbsoluteUrl, logError]);

	// Jira issue linked PRs (GitHub PR hydration).
	// Only enabled when the user has Jira available and the GitHub connector
	// authenticated. Hydration is lazy: getJiraIssueARIs returns [] when no
	// Jira issues are present in the result set, in which case the fetch
	// effect below short-circuits and no network request is made.
	const isJiraLinkedPRsEnabled = shouldHydrateJiraGitHubPRs(thirdPartyConfigs, availableProducts);
	const [linkedPRs, setLinkedPRs] = useState<IssueLinkedPRs>({});
	const { fireAnalyticsEvent: fireLinkedPRsAnalyticsEvent } = useAnalytics();

	const allIssueAris = useMemo(
		() => (isJiraLinkedPRsEnabled ? getJiraIssueARIs(edges) : []),
		[edges, isJiraLinkedPRsEnabled],
	);

	// Track which ARIs we've already requested to avoid duplicate fetches on pagination.
	// When edges grow (e.g., user loads more results), we only fetch PRs for newly added issues.
	// Uses a ref rather than state to avoid triggering re-renders or effect cycles.
	const fetchedArisRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (!isJiraLinkedPRsEnabled) {
			return;
		}
		const newAris = allIssueAris.filter((ari) => !fetchedArisRef.current.has(ari));
		if (newAris.length === 0) {
			return;
		}
		for (const ari of newAris) {
			fetchedArisRef.current.add(ari);
		}
		fetchAndUpdateJiraLinkedPRs(
			newAris,
			cloudId,
			aggAbsoluteUrl,
			setLinkedPRs,
			logError,
			fireLinkedPRsAnalyticsEvent,
		);
	}, [
		allIssueAris,
		cloudId,
		aggAbsoluteUrl,
		logError,
		isJiraLinkedPRsEnabled,
		fireLinkedPRsAnalyticsEvent,
	]);

	const are3pResultsShown = useMemo(
		() => are3pItemsInResults(edges, thirdPartyConfigs),
		[edges, thirdPartyConfigs],
	);
	// An explicit "Comments" type-filter selection bypasses the per-product embedded-comments
	// rollout gates, so analytics has to account for it too - mirrors search-graph-document.
	const isCommentsFilterActive = useMemo(
		() => getSelectedFilters().selectedEntities.includes(TypeFilterValueKey.Comment),
		[getSelectedFilters],
	);
	const resultsShown = useMemo(
		() => getResultsShown(edges, thirdPartyConfigs, isCommentsFilterActive),
		[edges, thirdPartyConfigs, isCommentsFilterActive],
	);

	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const wasContentClearedBySpaceChange = useWasContentClearedBySpaceChange();
	const searchActions = useSearchActions();

	const clearSpaceFilter = useCallback(async () => {
		await searchActions.clearSelectFilterOptions({ filterId: spaceFilter.id });
	}, [searchActions]);

	const intl = useIntl();
	const intentDetectionTriggered = useMemo(
		() => isIntentDetectionTriggeredForExperiment(primaryProduct, intl.locale),
		[primaryProduct, intl.locale],
	);

	const onResultsAnalyticsRun = useResultFieldDiagnostics(edges, primaryProduct);
	useSearchResultsAnalytics({
		are3pResultsShown,
		backendExperimentRef,
		clearReferrer,
		cloudId,
		commonAttributes,
		edges,
		endCursor: cursor,
		experimentConfig,
		getSelectedFilters,
		intentDetectionTriggered,
		isJiraIssueIndexed,
		isLoadingNext,
		isPaginatedQuery: hasPreviousRef.current,
		nonPrivacySafeAttributes,
		onResultsAnalyticsRun,
		productDataRegions,
		productEdition,
		productsWithTimeoutsFromErrors,
		queryReference,
		referrer,
		resultsForAnalytics,
		resultsShown,
		searchQuery,
		selectedSites,
	});

	useSearchResultsVitalStats({
		edges,
		searchSessionId,
		isCQL: false,
		isCachedResult: isCachedResult ?? undefined,
	});

	// Fire the Teams impersonation exposure at most once per mount. Without the ref the effect
	// re-runs on every `isLoadingNext` true -> false transition, so each "load more" page would log
	// a duplicate exposure for the same search session and inflate the experiment's denominator.
	const hasFiredTeamsExposureRef = useRef(false);
	useEffect(() => {
		if (isLoadingNext || hasFiredTeamsExposureRef.current) {
			return;
		}

		if (
			thirdPartyConfigs?.[ProductKeys3P.Teams]?.impersonationEnabled &&
			selectedProduct === undefined
		) {
			hasFiredTeamsExposureRef.current = true;
			triggerExposureForTeamsImpersonatedSearchExp();
		}
	}, [isLoadingNext, selectedProduct, thirdPartyConfigs]);

	// Update if there are no results
	useEffect(() => {
		setResultsState(edges.length === 0 ? ResultsStates.NO_RESULTS : ResultsStates.RESULTS);
	}, [edges, setResultsState]);

	useAfterQueryPerformanceTiming(edges, dataAvailableAt);

	const queryInfo = bodyData?.queryInfo;
	const containsSearchResults = edges.length > 0;
	const isResultRowSubscriptionHoistingEnabled =
		useResultRowSubscriptionHoistingExperiment(containsSearchResults);

	const debugResults: DebugResult[] = debugPortalRef?.current
		? edges.map((edge) => edge?.node ?? {})
		: [];
	const traceId = bodyData?.traceId ?? undefined;

	const body = (
		<>
			<UFOCustomCohortData dataKey="wordCount" value={commonAttributes.wordCount} />
			{isCachedResult !== undefined && (
				<UFOCustomCohortData dataKey="asyncQueryCachedResult" value={!!isCachedResult} />
			)}
			{queryInfo &&
				autoCorrectPortalRef &&
				autoCorrectPortalRef.current &&
				createPortal(
					<AutoCorrection node={queryInfo} appMode={appMode} cloudId={cloudId} />,
					autoCorrectPortalRef.current,
					'search-results-auto-correction',
				)}
			{debugPortalRef?.current &&
				createPortal(
					<DebugOverridesPanel
						traceId={traceId}
						backendExperiment={backendExperimentRef.current ?? undefined}
						results={debugResults}
					/>,
					debugPortalRef.current,
				)}
			<ProductOauthUpgradeBanner
				selectedProduct={selectedProduct}
				thirdPartyConfigs={thirdPartyConfigs}
			/>
			{/* The new Rovo Home experience renders Jira issues and projects in a table view in FPS. */}
			<ResultDerivedData>
				{({ preparedResultAttributes, isCommentsFilterActive, productConfigs }) =>
					isLiteMode ? (
						<SearchResultsListLiteRenderer
							preparedResultAttributes={preparedResultAttributes}
							intentDetectionTriggered={intentDetectionTriggered}
							isCommentsFilterActive={isCommentsFilterActive}
							productConfigs={productConfigs}
							containsSearchResults={containsSearchResults}
							edgesToDisplay={edgesToDisplay}
							renderConnectorPromo={renderConnectorPromo}
							cloudId={cloudId}
							appMode={appMode}
							searchQuery={searchQuery}
							thirdPartyConfigs={thirdPartyConfigs}
							selectedSites={selectedSites}
							selectedProduct={selectedProduct}
							primaryProduct={primaryProduct}
							primarySubProduct={primarySubProduct}
							searchSessionId={searchSessionId}
							originalQuery={queryInfo?.originalQuery}
							availableProducts={availableProducts}
							logError={logError}
							teamNames={teamNames}
							issueStatuses={issueStatuses}
							linkedPRs={linkedPRs}
							pastMeetingOccurrences={pastMeetingOccurrences}
							smartAnswersPortalRef={smartAnswersPortalRef}
							mainContentWidth={mainContentWidth}
							wasContentClearedBySpaceChange={wasContentClearedBySpaceChange}
							clearSpaceFilter={clearSpaceFilter}
							clearFilters={clearFilters}
							getSelectedFilters={getSelectedFilters}
							cursor={cursor}
							isLoadingNext={isLoadingNext}
							fetchNextPage={fetchNextPage}
							isResultRowSubscriptionHoistingEnabled={isResultRowSubscriptionHoistingEnabled}
						/>
					) : (
						<SearchResultsListDefaultRenderer
							preparedResultAttributes={preparedResultAttributes}
							intentDetectionTriggered={intentDetectionTriggered}
							isCommentsFilterActive={isCommentsFilterActive}
							productConfigs={productConfigs}
							containsSearchResults={containsSearchResults}
							edgesToDisplay={edgesToDisplay}
							renderConnectorPromo={renderConnectorPromo}
							cloudId={cloudId}
							appMode={appMode}
							searchQuery={searchQuery}
							thirdPartyConfigs={thirdPartyConfigs}
							selectedSites={selectedSites}
							selectedProduct={selectedProduct}
							primaryProduct={primaryProduct}
							primarySubProduct={primarySubProduct}
							searchSessionId={searchSessionId}
							originalQuery={queryInfo?.originalQuery}
							availableProducts={availableProducts}
							logError={logError}
							teamNames={teamNames}
							issueStatuses={issueStatuses}
							linkedPRs={linkedPRs}
							pastMeetingOccurrences={pastMeetingOccurrences}
							smartAnswersPortalRef={smartAnswersPortalRef}
							mainContentWidth={mainContentWidth}
							wasContentClearedBySpaceChange={wasContentClearedBySpaceChange}
							clearSpaceFilter={clearSpaceFilter}
							clearFilters={clearFilters}
							getSelectedFilters={getSelectedFilters}
							cursor={cursor}
							isLoadingNext={isLoadingNext}
							fetchNextPage={fetchNextPage}
							isResultRowSubscriptionHoistingEnabled={isResultRowSubscriptionHoistingEnabled}
						/>
					)
				}
			</ResultDerivedData>
		</>
	);

	return body;
};

export const are3pItemsInResults: (
	edges: readonly Minimal3pItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => boolean = (
	edges: readonly Minimal3pItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	return !!edges?.some(({ node }) => {
		const productKey =
			getProductFromProviderId(thirdPartyConfigs, node?.providerId) ||
			getIntegrationKey(node?.integrationId);
		if (!productKey) {
			// if no integration id it's a 1p
			return false;
		}
		return is3pProductKeyForAnalytics(productKey);
	});
};

type MinimalCompassComponentEdge = {
	node?: {
		id: string;
		__typename?: string;
		ownerId?: string | null;
	} | null;
};

function getCompassTeamIds(
	edges: readonly MinimalCompassComponentEdge[],
	selectedProduct?: ProductKeys,
): { teamIds: string[]; nodeIdMap: Record<string, string | null> } {
	if (selectedProduct !== ProductKeys.Compass && selectedProduct !== undefined) {
		return { teamIds: [], nodeIdMap: {} };
	}
	const nodeIdMap: Record<string, string | null> = {};
	const ids = new Set<string>();
	for (let i = 0; i < edges.length; i++) {
		const node = edges[i]?.node;
		let ownerId = node?.ownerId;
		if (isSearchCompassComponent(node)) {
			const teamId = getTeamId(ownerId);
			if (teamId) {
				nodeIdMap[node.id] = teamId;
				ids.add(teamId);
			} else {
				nodeIdMap[node.id] = null;
			}
		}
	}
	return { teamIds: Array.from(ids), nodeIdMap };
}

type MinimalJiraIssueComponentEdge = {
	node?: {
		id: string;
		__typename?: string;
		url?: string | null;
		issue?: {
			webUrl?: string | null;
		} | null;
	} | null;
};

type MinimalPastMeetingVideoEdge =
	| {
			node:
				| {
						__typename: string;
						id: string;
						integrationId?: string | null;
				  }
				| null
				| undefined;
	  }
	| null
	| undefined;

/**
 * Extracts ARIs for recurring meeting video results from the result edges.
 * These are SearchResultGraphDocument nodes with a supported integration ID whose
 * entity ARI resource type is 'video' (e.g. ari:cloud:graph::video/teams/...).
 *
 * We narrow by ARI resource type rather than `subtype` because `subtype` is null
 * in production for MS Teams meeting transcripts.
 */
function getPastMeetingVideoHydrationItems(
	edges: readonly MinimalPastMeetingVideoEdge[],
	includeZoomVideos: boolean,
): { videoAri: string; queryType: PastMeetingOccurrencesQueryType }[] {
	const seenAris = new Set<string>();
	const items: { videoAri: string; queryType: PastMeetingOccurrencesQueryType }[] = [];
	for (const edge of edges) {
		const node = edge?.node;
		const queryType = getPastMeetingOccurrencesQueryType(node?.integrationId);
		if (
			node?.id &&
			node.__typename === 'SearchResultGraphDocument' &&
			(queryType === 'teams' || (includeZoomVideos && queryType === 'zoom')) &&
			node.id.includes('::video/')
		) {
			if (!seenAris.has(node.id)) {
				seenAris.add(node.id);
				items.push({ videoAri: node.id, queryType });
			}
		}
	}
	return items;
}

function getPastMeetingOccurrencesQueryType(
	integrationId: string | null | undefined,
): PastMeetingOccurrencesQueryType | undefined {
	if (integrationId === MICROSOFT_INTEGRATION_ARI) {
		return 'teams';
	}
	if (integrationId === ZOOM_INTEGRATION_ARI) {
		return 'zoom';
	}
	return undefined;
}

function getJiraIssueARIs(edges: readonly MinimalJiraIssueComponentEdge[]): string[] {
	const issueArisSet = new Set<string>();
	for (let i = 0; i < edges.length; i++) {
		const node = edges[i]?.node;
		if (node?.id && isSearchJiraIssue(node)) {
			issueArisSet.add(node.id);
		}
	}
	return Array.from(issueArisSet);
}

function getJiraIssueHydrationItems(
	edges: readonly MinimalJiraIssueComponentEdge[],
): { id: string; issueKey: string }[] {
	const items: { id: string; issueKey: string }[] = [];
	for (const edge of edges) {
		const node = edge.node;
		if (node?.id && isSearchJiraIssue(node)) {
			const issueKey =
				node.issue?.webUrl?.split('/').slice(-1)[0] || node.url?.split('/').slice(-1)[0];
			if (issueKey) {
				items.push({ id: node.id, issueKey });
			}
		}
	}
	return items;
}

type Minimal3pItemsEdge = {
	node?: {
		__typename?: string;
		id: string;
		providerId?: string | null;
		integrationId?: string | null;
	} | null;
};

type MinimalLinkedResultItemsEdge = {
	node?: {
		__typename?: string;
		id: string;
		providerId?: string | null;
		integrationId?: string | null;
		datasourceId?: string | null;
		description: string | null;
		linkedResults?: readonly { id: string; category: string }[] | null;
		linkedEntities?: readonly { id: string }[] | null;
		connectorType?: string | null;
		connectorId?: string | null;
		connectorProduct?: string | null;
		repository?: { scm?: string | null } | null;
	} | null;
};

/**
 * How many embedded comments the `EmbeddedComments` surface will draw for a result.
 *
 * Returns `undefined` for rows that can never show them, so the caller omits the attribute
 * (absent reads as NULL downstream, distinct from `0`).
 *
 * Keep the gate in sync with `isEmbeddedCommentsEnabled` in `search-graph-document/index.tsx`.
 */
const getEmbeddedCommentsMetadata = (
	node: MinimalLinkedResultItemsEdge['node'],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	isCommentsFilterActive: boolean,
): { embeddedCommentsShown: number } | undefined => {
	// The base 3P graph type, not the row discriminator - the renderer is chosen by the ARI's
	// resourceType (see getGraphRenderer in search-graph-result).
	if (node?.__typename !== 'SearchResultGraphDocument') {
		return undefined;
	}

	let resourceType: string;
	try {
		resourceType = AnyAri.parse(node.id).resourceType;
	} catch {
		// Code results carry git SHAs rather than ARIs - see TODO(QS-10212) below.
		return undefined;
	}

	// Slack messages and GitHub PRs also populate linkedEntities but render them via ThreadBody.
	if (resourceType !== ResourceType.DOCUMENT) {
		return undefined;
	}

	// Not the local productKey: that resolves SharePoint to 'microsoft' and picks the wrong gate.
	const product = get3PProductFromProviderIdOrIntegrationId({
		thirdPartyConfigs,
		providerId: node.providerId,
		integrationId: node.integrationId,
		resourceType,
	});

	// Confluence DC documents route to SearchGraphConfluenceDc, which has no comments footer.
	if (product === ProductKeys3P.ConfluenceDC) {
		return undefined;
	}

	const isEmbeddedCommentsEnabled =
		isCommentsFilterActive ||
		(product === ProductKeys.Sharepoint
			? fg('rovo_fps_sharepoint_embedded_comments')
			: fg('enable_3p_embedded_comments_search'));

	return {
		embeddedCommentsShown: isEmbeddedCommentsEnabled
			? Math.min(node.linkedEntities?.length ?? 0, EMBEDDED_COMMENTS_DEFAULT_MAX)
			: 0,
	};
};

export const getResultsShown: (
	edges: readonly MinimalLinkedResultItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	isCommentsFilterActive?: boolean,
) => ResultShown[] = (
	edges: readonly MinimalLinkedResultItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	isCommentsFilterActive: boolean = false,
): ResultShown[] => {
	return (
		edges?.map(({ node }, index) => {
			const isCodeResult = node?.__typename === 'SearchResultGraphCode' && isAnyCodeSearchEnabled();

			// Code results are attributed by their host (repository.scm), and left unattributed
			// rather than guessed when it is absent or unmapped.
			const productKey = isCodeResult
				? scmToProductKey(node?.repository?.scm)
				: getProductFromProviderId(thirdPartyConfigs, node?.providerId) ||
					getIntegrationKey(node?.integrationId);

			// TODO(QS-10212): GitHub code results currently have git SHAs (not ARIs) and no connector
			// fields in the aggregator payload, so skip ARI-based product detection and short-circuit to
			// GitHub. Revisit when the aggregator schema gap is closed and other 3P code providers
			// (GitLab, Bitbucket, etc.) are added.
			const [firstPartyProduct] = isCodeResult
				? [undefined]
				: getProductAndIDFromARI(node?.id || '');

			let hasLinkInDescription = false;
			if (!isCodeResult) {
				const resultAri = AnyAri.parse(node?.id || '');
				if (resultAri.resourceOwner in products1P) {
					const links = getLinksFromText(node?.description ?? '');
					if (links && links.length > 0) {
						hasLinkInDescription = true;
					}
				}
			}

			const shouldShowBoostedLinks = node?.linkedResults
				? node?.linkedResults.length > 0 && index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
				: undefined;

			const connectorSourceType = getResultConnectorSource({
				connectorType: node?.connectorType,
				resultNodeId: node?.id || '',
				productKey: productKey,
				thirdPartyConfigs,
			});

			const embeddedCommentsMetadata = getEmbeddedCommentsMetadata(
				node,
				thirdPartyConfigs,
				isCommentsFilterActive,
			);

			// Excludes code results (git SHAs, not ARIs - see PR description).
			const connectorInfo =
				productKey && !isCodeResult
					? {
							provider_id: node?.providerId || undefined,
							datasource_id: node?.datasourceId || undefined,
							connector_id: node?.connectorId || undefined,
							connector_position: index,
							connector_product: node?.connectorProduct || undefined,
							connector_type: node?.connectorType || undefined,
							ari: node?.id,
							metadata: {
								// Add connectorWorkspaceId when available from backend
								// For now, only include fanoutResultsShown if present
								...(node?.linkedResults &&
								node?.linkedResults.length > 0 &&
								index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
									? {
											fanOutResultsShown: node.linkedResults
												.slice(0, LINKED_RESULTS_DEFAULT_EXPAND_SIZE)
												.map((link) => {
													let is3P = false;
													try {
														is3P = !(AnyAri.parse(link.id).resourceOwner in products1P);
													} catch {
														is3P = false;
													}
													return {
														id: link.id,
														category: link.category,
														is3P,
													};
												}),
										}
									: {}),
							},
						}
					: undefined;

			return {
				id: node?.id,
				position: index,
				product: productKey || firstPartyProduct,
				metadata: {
					connectorType: connectorSourceType,
					boostedLinksShown: shouldShowBoostedLinks,
					fanoutResultsShown:
						node?.linkedResults &&
						node?.linkedResults.length > 0 &&
						index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
							? node.linkedResults.slice(0, LINKED_RESULTS_DEFAULT_EXPAND_SIZE).map((link) => {
									let is3P = false;
									try {
										is3P = !(AnyAri.parse(link.id).resourceOwner in products1P);
									} catch {
										is3P = false;
									}
									return {
										id: link.id,
										category: link.category,
										is3P,
									};
								})
							: undefined,
					hasLinkInDescription,
					// Omitted for rows that can never show embedded comments, so absent reads as
					// NULL downstream rather than 0.
					...(embeddedCommentsMetadata ?? {}),
				},
				connectorInfo,
			} as ResultShown;
		}) || []
	);
};

type MinimalAnalyticsEdge = {
	node?: {
		id: string;
		__typename?: string;
		integrationId?: string | null;
		providerId?: string | null;
		socialSignal?: any | null;
		connectorType?: string | null;
		connectorId?: string | null;
		connectorProduct?: string | null;
		repository?: { scm?: string | null } | null;
	} | null;
};

/**
 * Relay and RSS results have different types
 * Shared code (like result analytics) has typing assuming RSS
 * We solve this by building interfaces that accept both, such as IAnalyticsSearchPageResults
 *
 * We likely need a better approach for coalescing RSS and Relay result node typing, for shared code as we migrate away from RSS
 */
const transformResultsForAnalytics = (
	edges: MinimalAnalyticsEdge[],
): IAnalyticsSearchPageResults => {
	const nodes =
		edges
			?.filter((edge) => !!edge?.node) // drop undefined nodes
			?.map((edge) => {
				const node = edge.node;
				return {
					id: node?.id || '',
					__typename: node?.__typename || '',
					integrationId: node?.integrationId || '',
					providerId: node?.providerId || '',
					socialSignal: node?.socialSignal?.signal ? node?.socialSignal : null,
					connectorType: node?.connectorType || null,
					repository: node?.repository || null,
				};
			}) || [];

	return {
		edges: nodes.map((node) => {
			return { node };
		}),
	};
};
